// Metric read plumbing for dashboard widget tiles: the `useMetric` query hook
// over `POST /v1/metrics:query` and the agent-scope overlay. Every
// agent-filterable metric read carries `agent_id`; which metrics admit the
// filter comes from the `METRIC_METADATA` registry mirror, matching the
// server's per-metric filter validation.

import { useQuery, type UseQueryResult } from "@tanstack/react-query";

import type { components } from "../api/schema";
import { apiClient } from "./apiClient";
import { METRIC_METADATA } from "./dashboardWidgets";
import {
  metricCacheKey,
  type MetricBucket,
  type MetricDimension,
  type MetricName,
  type MetricsQueryResponse,
  type TimeWindow,
} from "./dashboardMetrics";
import { ApiRequestError, requestErrorDescription } from "./errors";
import { queryKeys } from "./queryKeys";

type MetricFilters = components["schemas"]["MetricFilters"];

export const EMPTY_METRIC_FILTERS: MetricFilters = {
  agent_id: null,
  agent_version_id: null,
  batch_id: null,
  event_type: null,
  fail_class: null,
  initiator: null,
  kind: null,
  marker_id: null,
  modality: null,
  name: null,
  persona_id: null,
  provider: null,
  scenario_id: null,
  scenario_set_id: null,
  source: null,
  transcript_status: null,
  transport: null,
};

export function metricSupportsAgentFilter(metric: MetricName): boolean {
  return METRIC_METADATA[metric].filters.includes("agent_id");
}

export interface MetricSpec {
  agentId?: string;
  metric: MetricName;
  bucket: MetricBucket;
  filters?: Partial<MetricFilters>;
  group_by: MetricDimension[];
  window: TimeWindow;
  enabled?: boolean;
}

// Identical specs dedup in the query cache via `metricCacheKey`, so the same
// widget on two boards (or a widget and its editor preview) costs one read.
export function useMetric(
  spec: MetricSpec,
): UseQueryResult<MetricsQueryResponse> {
  const filters =
    spec.agentId && metricSupportsAgentFilter(spec.metric)
      ? { ...EMPTY_METRIC_FILTERS, ...spec.filters, agent_id: spec.agentId }
      : spec.filters
        ? { ...EMPTY_METRIC_FILTERS, ...spec.filters }
        : undefined;
  return useQuery({
    enabled: spec.enabled !== false,
    queryKey: queryKeys.metrics.list(
      metricCacheKey({
        agent_id: filters?.agent_id ?? undefined,
        metric: spec.metric,
        bucket: spec.bucket,
        filters,
        group_by: spec.group_by,
        window: spec.window,
      }),
    ),
    queryFn: async () => {
      const { data, error } = await apiClient.POST("/v1/metrics:query", {
        body: {
          aggregate: "default",
          metric: spec.metric,
          bucket: spec.bucket,
          filters,
          group_by: spec.group_by,
          time_range: { start: spec.window.start, end: spec.window.end },
        },
      });
      if (error) {
        throw new ApiRequestError(error);
      }
      return data;
    },
  });
}

// Generic metric-query view-model: resolves a `useMetric` result into a
// loading/error/data/notice shape a WidgetFrame can render without touching
// react-query. Shared by the /settings/usage page; the dashboard board renders
// its self-describing tiles directly and does not use this.
export interface WidgetData<T> {
  isLoading: boolean;
  error: string | null;
  data: T;
  notice?: string | null;
}

export function toWidget<T>(
  query: UseQueryResult<MetricsQueryResponse>,
  transform: (response: MetricsQueryResponse) => T,
  empty: T,
): WidgetData<T> {
  return {
    isLoading: query.isPending,
    error: query.isError ? requestErrorDescription(query.error) : null,
    data: query.data ? transform(query.data) : empty,
    notice: query.data?.notice ?? null,
  };
}
